import { useState, useRef, useEffect } from "react";
import { getCurrentBrandInfo } from "./db/index.js";

export default function NavBar({ G, onLogout, onNav, darkMode, setDarkMode, step, user }) {
  const [dropOpen, setDropOpen] = useState(false);
  const [brand, setBrand] = useState(null);
  const dropRef = useRef();

  // Load brand data from database
    const refreshData = async () => {
    try {
      const brandInfo = await getCurrentBrandInfo();
      setBrand(brandInfo || {});
    } catch (error) {
      // ✅ DIFFERENTIATE: Permission errors vs actual failures
      if (error.code === 'PERMISSION_DENIED') {
        // Expected for FREE tier - silent fallback
        console.log('ℹ️ Brand data access restricted to paid tiers');
        setBrand({});
      } else if (error.message?.includes('Not authenticated')) {
        // Auth error - user logged out
        console.log('ℹ️ User not authenticated');
        setBrand({});
      } else {
        // Unexpected error - log it
        console.error("Failed to load navbar data:", error);
        setBrand({});
      }
    }
  };

  // Initial load on mount
  useEffect(() => {
    refreshData();
  }, []);

  // Listen for brand updates (from BrandSetup save)
  useEffect(() => {
    const handleBrandUpdate = () => {
      console.log("🔄 Brand updated, refreshing navbar...");
      refreshData();
    };

    window.addEventListener('brandUpdated', handleBrandUpdate);
    return () => window.removeEventListener('brandUpdated', handleBrandUpdate);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const h = (e) => { 
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false); 
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && dropOpen) {
        setDropOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [dropOpen]);

  const navBtn = (label, idx) => (
    <button
      key={label}
      onClick={() => onNav(idx)}
      aria-label={`Navigate to ${label.replace(/[^\w\s]/g, '')}`}
      aria-current={step === idx ? "page" : undefined}
      style={{
        background: step === idx ? G.accentBg : "transparent",
        color: step === idx ? G.accent : G.ink2,
        border: `2px solid ${step === idx ? G.accent : G.border}`,
        borderRadius: 12,
        padding: "8px 12px",
        fontSize: 13,
        fontWeight: step === idx ? 600 : 500,
        cursor: "pointer",
        transition: "all .15s",
        display: "flex",
        alignItems: "center",
        gap: 6
      }}
      onMouseEnter={e => {
        if (step !== idx) {
          e.currentTarget.style.background = G.accentBg;
          e.currentTarget.style.color = G.accent;
          e.currentTarget.style.borderColor = G.accent;
        }
      }}
      onMouseLeave={e => {
        if (step !== idx) {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = G.ink2;
          e.currentTarget.style.borderColor = G.border;
        }
      }}
    >
      {label}
    </button>
  );

  // Utility button (same style, no active state)
  const utilBtn = (icon, onClick, title) => (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        background: "transparent",
        color: G.ink2,
        border: `2px solid ${G.border}`,
        borderRadius: 12,
        padding: "8px 12px",
        fontSize: 13,
        fontWeight: 500,
        cursor: "pointer",
        transition: "all .15s",
        display: "flex",
        alignItems: "center",
        gap: 6
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = G.accentBg;
        e.currentTarget.style.color = G.accent;
        e.currentTarget.style.borderColor = G.accent;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = G.ink2;
        e.currentTarget.style.borderColor = G.border;
      }}
    >
      {icon}
    </button>
  );

  return (
    <div
      className="no-print"
      style={{
        background: G.surface,
        borderBottom: `1px solid ${G.border}`,
        padding: "0 24px",
        display: "flex",
        alignItems: "center",
        height: 58,
        gap: 6,
        position: "sticky",
        top: 0,
        zIndex: 100,
        boxShadow: G.shadow
      }}
    >
      {/* Profile Dropdown */}
      <div ref={dropRef} style={{ position: "relative", marginRight: 12 }}>
        <button
          onClick={() => setDropOpen(o => !o)}
          aria-label="Open profile menu"
          aria-expanded={dropOpen}
          aria-haspopup="true"
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            cursor: "pointer",
            border: `2px solid ${G.border}`,
            overflow: "hidden",
            background: G.surface2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            padding: 0
          }}
        >
          {brand?.logoId ? (
            <img 
              src={brand.logoId} 
              alt={`${brand.name || 'Brand'} logo`} 
              style={{ width: "100%", height: "100%", objectFit: "cover" }} 
            />
          ) : (
            <span style={{ fontSize: 18 }} role="img" aria-label="Default profile icon">👤</span>
          )}
        </button>

        {dropOpen && (
          <div
            role="menu"
            aria-label="Profile menu"
            style={{
              position: "absolute",
              left: 0,
              top: 48,
              background: G.surface,
              border: `1px solid ${G.border}`,
              borderRadius: 9,
              padding: "10px",
              minWidth: 240,
              boxShadow: G.shadow,
              zIndex: 200
            }}
          >
            {/* Header - Brand + Owner + Email */}
            <div
              style={{
                padding: "10px 12px",
                borderBottom: `1px solid ${G.border}`,
                marginBottom: 6
              }}
            >
              {/* Brand Name */}
              <div
                style={{
                  fontFamily: "'Lora', serif",
                  fontSize: 15,
                  fontWeight: 600,
                  color: brand?.name ? G.ink : G.muted,
                  marginBottom: 4,
                  lineHeight: 1.3,
                  fontStyle: brand?.name ? 'normal' : 'italic'
                }}
              >
                {brand?.name || "Your Brand Name"}
              </div>
              
              {/* Owner Name */}
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: brand?.owner ? G.ink2 : G.muted,
                  lineHeight: 1.3,
                  marginBottom: 2,
                  fontStyle: brand?.owner ? 'normal' : 'italic'
                }}
              >
                {brand?.owner || "Your Name"}
              </div>
              
              {/* Email */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  color: G.muted,
                  lineHeight: 1.3
                }}
              >
                {user?.email || ""}
              </div>
            </div>

            {/* Brand Info */}
            <button
              role="menuitem"
              onClick={() => { onNav(0); setDropOpen(false); }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: step === 0 ? G.accentBg : "transparent",
                border: "none",
                padding: "10px 14px",
                fontSize: 14,
                color: step === 0 ? G.accent : G.ink,
                fontWeight: step === 0 ? 600 : 500,
                cursor: "pointer",
                borderRadius: 6,
                marginBottom: 4,
                transition: "all .15s",
                fontFamily: "'Sora', sans-serif"
              }}
              onMouseEnter={e => e.currentTarget.style.background = step === 0 ? G.accentBg : G.surface2}
              onMouseLeave={e => e.currentTarget.style.background = step === 0 ? G.accentBg : "transparent"}
            >
              🏢 Brand Info
            </button>

            {/* Products */}
            <button
              role="menuitem"
              onClick={() => { onNav(1); setDropOpen(false); }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: step === 1 ? G.accentBg : "transparent",
                border: "none",
                padding: "10px 14px",
                fontSize: 14,
                color: step === 1 ? G.accent : G.ink,
                fontWeight: step === 1 ? 600 : 500,
                cursor: "pointer",
                borderRadius: 6,
                marginBottom: 4,
                transition: "all .15s",
                fontFamily: "'Sora', sans-serif"
              }}
              onMouseEnter={e => e.currentTarget.style.background = step === 1 ? G.accentBg : G.surface2}
              onMouseLeave={e => e.currentTarget.style.background = step === 1 ? G.accentBg : "transparent"}
            >
              📦 Products
            </button>

            {/* Customers (NEW) */}
            <button
              role="menuitem"
              onClick={() => { onNav(7); setDropOpen(false); }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: step === 7 ? G.accentBg : "transparent",
                border: "none",
                padding: "10px 14px",
                fontSize: 14,
                color: step === 7 ? G.accent : G.ink,
                fontWeight: step === 7 ? 600 : 500,
                cursor: "pointer",
                borderRadius: 6,
                marginBottom: 8,
                transition: "all .15s",
                fontFamily: "'Sora', sans-serif"
              }}
              onMouseEnter={e => e.currentTarget.style.background = step === 7 ? G.accentBg : G.surface2}
              onMouseLeave={e => e.currentTarget.style.background = step === 7 ? G.accentBg : "transparent"}
            >
              👥 Customers
            </button>

            {/* Separator */}
            <div style={{ borderTop: `1px solid ${G.border}`, margin: "8px 0" }} />

            {/* Settings */}
            <button
              role="menuitem"
              onClick={() => { onNav(6); setDropOpen(false); }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: step === 6 ? G.accentBg : "transparent",
                border: "none",
                padding: "10px 14px",
                fontSize: 14,
                color: step === 6 ? G.accent : G.ink,
                fontWeight: step === 6 ? 600 : 500,
                cursor: "pointer",
                borderRadius: 6,
                marginBottom: 8,
                transition: "all .15s",
                fontFamily: "'Sora', sans-serif"
              }}
              onMouseEnter={e => e.currentTarget.style.background = step === 6 ? G.accentBg : G.surface2}
              onMouseLeave={e => e.currentTarget.style.background = step === 6 ? G.accentBg : "transparent"}
            >
              ⚙️ Settings
            </button>

            {/* Separator */}
            <div style={{ borderTop: `1px solid ${G.border}`, margin: "8px 0" }} />

            {/* Logout */}
            <button
              role="menuitem"
              onClick={onLogout}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                padding: "8px 12px",
                fontSize: 13,
                fontWeight: 500,
                color: G.danger,
                cursor: "pointer",
                borderRadius: 6,
                transition: "all .15s",
                fontFamily: "'Sora', sans-serif"
              }}
              onMouseEnter={e => e.currentTarget.style.background = G.dangerBg}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              🚪 Log Out
            </button>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      {navBtn("📋 Billing", 2)}
      {navBtn("🕒 History", 4)}
      {navBtn("📊 Reports", 5)}

      {/* Dark Mode Toggle */}
      {utilBtn(
        darkMode ? "☀️" : "🌙",
        () => setDarkMode(!darkMode),
        `Switch to ${darkMode ? 'Light' : 'Dark'} Mode`
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />
      
      {/* App Name */}
      <span
        style={{
          fontFamily: "'Lora', serif",
          fontSize: 22,
          fontWeight: 1000,
          textTransform: "none",
          letterSpacing: "0.5px",
          color: G.ink,
          whiteSpace: "nowrap",
          cursor: "default"
        }}
      >
        Prime Invoices
      </span>
    </div>
  );
}